// components/Admin/Order-Management/ArtisanAdHocPackingSlip.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Printer,
  Download,
  X,
  Package,
  MapPin,
  User,
  Phone,
  Mail,
  Calendar,
  CreditCard,
  Loader,
  AlertCircle,
  QrCode,
  Plus,
  Trash2,
  Edit3,
  Shield,
  ClipboardList,
  ArrowLeft,
  Calculator,
  CheckCircle2,
  Sparkles
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import TantikaLogo from '../../../Assets/TantikaLogo.png';

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const ArtisanAdHocPackingSlip = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [qrError, setQrError] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [step, setStep] = useState(1);
  const printRef = useRef(null);

  // ========== State for API data ==========
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearchTerms, setProductSearchTerms] = useState({});
  const [productSearchResults, setProductSearchResults] = useState({});
  const [activeItemId, setActiveItemId] = useState(null);
  const [artisanName, setArtisanName] = useState('');
  const [artisanId, setArtisanId] = useState('');

  // ========== Refs for dropdowns ==========
  const productDropdownRefs = useRef({});

  // Form state
  const [formData, setFormData] = useState({
    orderNumber: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    shippingAddress: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'India'
    },
    orderDate: new Date().toISOString().split('T')[0],
    artisanName: '',
    artisanId: '',
    paymentMethod: 'offline',
    subtotal: '',
    tax: '',
    shippingCost: '',
    total: '',
    items: [
      { id: 1, name: '', quantity: 1, price: '', sku: '', productId: '' }
    ],
    status: 'confirmed',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  // ========== Close dropdowns when clicking outside ==========
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(productDropdownRefs.current).forEach((key) => {
        if (productDropdownRefs.current[key] &&
            !productDropdownRefs.current[key].contains(event.target)) {
          setActiveItemId(null);
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ========== Get Artisan Info from localStorage ==========
  const getArtisanInfo = () => {
    try {
      const userData = JSON.parse(localStorage.getItem('tantika_user') || '{}');

      const name = userData.username ||
                   userData.businessName ||
                   userData.fullName ||
                   userData.name ||
                   'Artisan';

      const id = userData.artisanId ||
                 userData._id ||
                 userData.id ||
                 '';

      return { name, id };
    } catch (error) {
      console.error('Error getting artisan info:', error);
      return { name: 'Artisan', id: '' };
    }
  };

  // ========== Fetch Artisan's Products ==========
  const fetchArtisanProducts = async () => {
    try {
      setLoadingProducts(true);
      const token = localStorage.getItem('tantika_token');

      if (!token) {
        console.error('No auth token found');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/artisan/products?page=1&limit=200`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        const productList = data.data?.products || data.data || [];
        setProducts(productList);

        const initialResults = {};
        formData.items.forEach(item => {
          initialResults[item.id] = productList.slice(0, 5);
        });
        setProductSearchResults(initialResults);
      }
    } catch (error) {
      console.error('Error fetching artisan products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  // ========== Search Products ==========
  const searchProducts = (searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
      return products.slice(0, 5);
    }
    const term = searchTerm.toLowerCase().trim();
    return products.filter(product =>
      product.name.toLowerCase().includes(term) ||
      (product.sku && product.sku.toLowerCase().includes(term)) ||
      (product.category && product.category.toLowerCase().includes(term))
    ).slice(0, 10);
  };

  // ========== Load data on mount ==========
  useEffect(() => {
    const { name, id } = getArtisanInfo();

    setArtisanName(name);
    setArtisanId(id);

    setFormData(prev => ({
      ...prev,
      artisanName: name,
      artisanId: id
    }));

    fetchArtisanProducts();
    generateQRCode();
  }, []);

  // ========== Update product search results ==========
  useEffect(() => {
    if (products.length > 0) {
      const initialResults = {};
      formData.items.forEach(item => {
        initialResults[item.id] = products.slice(0, 5);
      });
      setProductSearchResults(initialResults);
    }
  }, [products]);

  // Generate QR Code
  const generateQRCode = async () => {
    setLoading(true);
    setQrError(false);

    try {
      const qrText = `https://tantikacrafts.netlify.app`;
      const qrApiUrl = `https://quickchart.io/qr?text=${encodeURIComponent(qrText)}&size=200&margin=4&dark=1a1a2e&light=ffffff&format=png`;

      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = qrApiUrl;
      });

      setQrCodeUrl(qrApiUrl);
    } catch (error) {
      console.error('QR Code generation error:', error);
      setQrError(true);
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  // ========== Handle product selection ==========
  const handleProductSelect = (itemId, product) => {
    const updatedItems = formData.items.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          name: product.name,
          price: product.price.toString(),
          sku: product.sku || '',
          productId: product._id
        };
      }
      return item;
    });

    setFormData({
      ...formData,
      items: updatedItems
    });

    setProductSearchTerms(prev => ({
      ...prev,
      [itemId]: product.name
    }));

    setProductSearchResults(prev => ({
      ...prev,
      [itemId]: [product]
    }));

    setActiveItemId(null);
  };

  // ========== Handle product search term change ==========
  const handleProductSearchChange = (itemId, value) => {
    setProductSearchTerms(prev => ({
      ...prev,
      [itemId]: value
    }));

    const updatedItems = formData.items.map(item => {
      if (item.id === itemId) {
        return { ...item, name: value, productId: '' };
      }
      return item;
    });

    setFormData({
      ...formData,
      items: updatedItems
    });

    if (value && value.trim()) {
      const results = searchProducts(value);
      setProductSearchResults(prev => ({
        ...prev,
        [itemId]: results
      }));
    } else {
      setProductSearchResults(prev => ({
        ...prev,
        [itemId]: products.slice(0, 5)
      }));
    }
  };

  // ========== Handle product input focus ==========
  const handleProductFocus = (itemId) => {
    setActiveItemId(itemId);
    const searchTerm = productSearchTerms[itemId] || '';
    if (searchTerm.trim()) {
      const results = searchProducts(searchTerm);
      setProductSearchResults(prev => ({
        ...prev,
        [itemId]: results
      }));
    } else {
      setProductSearchResults(prev => ({
        ...prev,
        [itemId]: products.slice(0, 5)
      }));
    }
  };

  // Add item row
  const addItem = () => {
    const newId = formData.items.length > 0
      ? Math.max(...formData.items.map(i => i.id)) + 1
      : 1;
    setFormData({
      ...formData,
      items: [...formData.items, { id: newId, name: '', quantity: 1, price: '', sku: '', productId: '' }]
    });
    setProductSearchResults(prev => ({
      ...prev,
      [newId]: products.slice(0, 5)
    }));
    productDropdownRefs.current[newId] = React.createRef();
  };

  // Remove item row
  const removeItem = (id) => {
    if (formData.items.length <= 1) {
      alert('At least one item is required');
      return;
    }
    setFormData({
      ...formData,
      items: formData.items.filter(item => item.id !== id)
    });
    setProductSearchTerms(prev => {
      const newTerms = { ...prev };
      delete newTerms[id];
      return newTerms;
    });
    setProductSearchResults(prev => {
      const newResults = { ...prev };
      delete newResults[id];
      return newResults;
    });
    delete productDropdownRefs.current[id];
  };

  // Update item field
  const updateItem = (id, field, value) => {
    setFormData({
      ...formData,
      items: formData.items.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    });
  };

  // Update form field
  const updateField = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  // Update address field
  const updateAddress = (field, value) => {
    setFormData({
      ...formData,
      shippingAddress: {
        ...formData.shippingAddress,
        [field]: value
      }
    });
  };

  // Auto-calculate total
  const calculateTotal = () => {
    const subtotal = formData.items.reduce((sum, item) => {
      const price = parseFloat(item.price) || 0;
      const qty = parseInt(item.quantity) || 0;
      return sum + (price * qty);
    }, 0);

    const tax = parseFloat(formData.tax) || 0;
    const shipping = parseFloat(formData.shippingCost) || 0;
    const total = subtotal + tax + shipping;

    setFormData({
      ...formData,
      subtotal: subtotal.toFixed(2),
      total: total.toFixed(2)
    });
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.orderNumber.trim()) newErrors.orderNumber = 'Order number is required';
    if (!formData.customerName.trim()) newErrors.customerName = 'Customer name is required';
    if (!formData.customerPhone.trim()) newErrors.customerPhone = 'Customer phone is required';
    if (!formData.shippingAddress.street.trim()) newErrors.street = 'Street address is required';
    if (!formData.shippingAddress.city.trim()) newErrors.city = 'City is required';
    if (!formData.shippingAddress.state.trim()) newErrors.state = 'State is required';
    if (!formData.shippingAddress.postalCode.trim()) newErrors.postalCode = 'Postal code is required';
    if (parseFloat(formData.total) <= 0) newErrors.total = 'Total amount must be greater than 0';

    formData.items.forEach((item, index) => {
      if (!item.name.trim()) {
        newErrors[`item_${index}_name`] = 'Item name is required';
      }
      if (parseFloat(item.price) <= 0) {
        newErrors[`item_${index}_price`] = 'Valid price is required';
      }
      if (parseInt(item.quantity) <= 0) {
        newErrors[`item_${index}_quantity`] = 'Quantity must be at least 1';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Generate packing slip
  const handleGenerate = () => {
    if (!validateForm()) {
      alert('Please fix all errors before generating the packing slip.');
      setStep(2);
      return;
    }
    setShowForm(false);
  };

  // Go back to form
  const handleEdit = () => {
    setShowForm(true);
  };

  // Generate PDF
  const handleGeneratePDF = async () => {
    if (!printRef.current) return;

    try {
      setGeneratingPDF(true);
      await new Promise(resolve => setTimeout(resolve, 500));

      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a5');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`packing-slip-${formData.orderNumber}.pdf`);

    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Print
  const handlePrint = () => {
    window.print();
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-saffron-100 text-saffron-700',
      'confirmed': 'bg-clay-100 text-clay-700',
      'processing': 'bg-cocoa-100 text-cocoa-700',
      'ready_to_ship': 'bg-cyan-100 text-cyan-800',
      'shipped': 'bg-blue-100 text-blue-800',
      'out_for_delivery': 'bg-teal-100 text-teal-800',
      'delivered': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pending',
      'confirmed': 'Confirmed',
      'processing': 'Processing',
      'ready_to_ship': 'Ready to Ship',
      'shipped': 'Shipped',
      'out_for_delivery': 'Out for Delivery',
      'delivered': 'Delivered',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status || 'Unknown';
  };

  const steps = [
    { id: 1, label: 'Details', icon: User },
    { id: 2, label: 'Items & Payment', icon: ClipboardList },
    { id: 3, label: 'Review', icon: CheckCircle2 }
  ];

  const inputClass = "w-full px-4 py-2.5 border border-cream-300 bg-white rounded-xl focus:ring-2 focus:ring-clay-500 focus:border-transparent outline-none transition-all placeholder:text-cocoa-400 text-cocoa-800";
  const inputErrorClass = "w-full px-4 py-2.5 border border-red-300 bg-white rounded-xl focus:ring-2 focus:ring-red-400 focus:border-transparent outline-none transition-all text-cocoa-800";
  const labelClass = "block text-sm font-semibold text-cocoa-800 mb-1.5";

  // ========== Section header ==========
  const SectionHeader = ({ icon: Icon, title, subtitle, bar }) => (
    <div className="flex items-center gap-3 mb-4">
      <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br ${bar || 'from-clay-500 to-saffron-500'} text-white shadow-warm shrink-0`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <h4 className="font-display font-bold text-cocoa-900 leading-tight">{title}</h4>
        {subtitle && <p className="text-xs text-cocoa-500">{subtitle}</p>}
      </div>
    </div>
  );

  // Render form
  const renderForm = () => (
    <div className="p-6 md:p-8 overflow-y-auto max-h-[calc(95vh-140px)] scrollbar-thin">
      <div className="max-w-3xl mx-auto">
        {/* Artisan welcome banner */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-clay-500 via-clay-400 to-saffron-500 text-white p-5 mb-6 shadow-warm">
          <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
          <div className="absolute -bottom-10 left-24 w-40 h-40 bg-saffron-300/20 rounded-full blur-2xl" />
          <div className="relative flex items-center gap-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white/15 backdrop-blur border border-white/20 shrink-0">
              <Shield className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-cream-100/90 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" />
                Creating for your shop
              </p>
              <p className="font-display font-bold text-lg leading-tight">{artisanName || 'Loading...'}</p>
              <p className="text-xs text-cream-100/90 mt-0.5">
                {artisanId ? `Artisan ID: ${artisanId.slice(-8).toUpperCase()}` : 'You will only see your own products below'}
              </p>
            </div>
          </div>
        </div>

        {/* Step indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-0 mb-2">
            {steps.map((s, i) => {
              const Icon = s.icon;
              const isActive = step === s.id;
              const isDone = s.id < step;
              return (
                <div key={s.id} className="flex items-center">
                  {i > 0 && (
                    <div className={`w-10 md:w-16 h-0.5 mx-1 md:mx-2 rounded-full ${s.id <= step ? 'bg-gradient-to-r from-clay-500 to-saffron-500' : 'bg-cream-200'}`} />
                  )}
                  <button
                    type="button"
                    onClick={() => s.id < step && setStep(s.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 ${
                      isActive
                        ? 'bg-gradient-to-r from-clay-500 to-saffron-500 text-white shadow-warm'
                        : isDone
                          ? 'bg-clay-100 text-clay-700 cursor-pointer'
                          : 'bg-cream-100 text-cocoa-400'
                    }`}
                  >
                    {isDone ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                    <span className="hidden sm:inline">{s.label}</span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {step === 1 && (
          <div className="space-y-6 animate-fadeInUp">
            {/* Order Details */}
            <div className="bg-white rounded-2xl border border-cream-200 shadow-sm overflow-hidden">
              <div className="h-1.5 bg-gradient-to-r from-clay-500 to-saffron-500" />
              <div className="p-5 md:p-6">
                <SectionHeader icon={Calendar} title="Order Details" subtitle="Reference information for this slip" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>
                      Order Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.orderNumber}
                      onChange={(e) => updateField('orderNumber', e.target.value)}
                      className={errors.orderNumber ? inputErrorClass : inputClass}
                      placeholder="e.g., OFFLINE-001"
                    />
                    {errors.orderNumber && (
                      <p className="text-red-500 text-xs mt-1">{errors.orderNumber}</p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>Order Date</label>
                    <input
                      type="date"
                      value={formData.orderDate}
                      onChange={(e) => updateField('orderDate', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Details */}
            <div className="bg-white rounded-2xl border border-cream-200 shadow-sm overflow-hidden">
              <div className="h-1.5 bg-gradient-to-r from-cocoa-500 to-cocoa-400" />
              <div className="p-5 md:p-6">
                <SectionHeader icon={User} title="Customer Details" subtitle="Who is receiving the package" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.customerName}
                      onChange={(e) => updateField('customerName', e.target.value)}
                      className={errors.customerName ? inputErrorClass : inputClass}
                      placeholder="Customer name"
                    />
                    {errors.customerName && (
                      <p className="text-red-500 text-xs mt-1">{errors.customerName}</p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.customerPhone}
                      onChange={(e) => updateField('customerPhone', e.target.value)}
                      className={errors.customerPhone ? inputErrorClass : inputClass}
                      placeholder="Phone number"
                    />
                    {errors.customerPhone && (
                      <p className="text-red-500 text-xs mt-1">{errors.customerPhone}</p>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelClass}>Email</label>
                    <input
                      type="email"
                      value={formData.customerEmail}
                      onChange={(e) => updateField('customerEmail', e.target.value)}
                      className={inputClass}
                      placeholder="Email address"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="bg-white rounded-2xl border border-cream-200 shadow-sm overflow-hidden">
              <div className="h-1.5 bg-gradient-to-r from-clay-500 to-rose-400" />
              <div className="p-5 md:p-6">
                <SectionHeader icon={MapPin} title="Shipping Address" subtitle="Where the package should be delivered" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className={labelClass}>
                      Street <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.shippingAddress.street}
                      onChange={(e) => updateAddress('street', e.target.value)}
                      className={errors.street ? inputErrorClass : inputClass}
                      placeholder="Street address"
                    />
                    {errors.street && (
                      <p className="text-red-500 text-xs mt-1">{errors.street}</p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>
                      City <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.shippingAddress.city}
                      onChange={(e) => updateAddress('city', e.target.value)}
                      className={errors.city ? inputErrorClass : inputClass}
                      placeholder="City"
                    />
                    {errors.city && (
                      <p className="text-red-500 text-xs mt-1">{errors.city}</p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>
                      State <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.shippingAddress.state}
                      onChange={(e) => updateAddress('state', e.target.value)}
                      className={errors.state ? inputErrorClass : inputClass}
                      placeholder="State"
                    />
                    {errors.state && (
                      <p className="text-red-500 text-xs mt-1">{errors.state}</p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>
                      Postal Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.shippingAddress.postalCode}
                      onChange={(e) => updateAddress('postalCode', e.target.value)}
                      className={errors.postalCode ? inputErrorClass : inputClass}
                      placeholder="Postal code"
                    />
                    {errors.postalCode && (
                      <p className="text-red-500 text-xs mt-1">{errors.postalCode}</p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>Country</label>
                    <input
                      type="text"
                      value={formData.shippingAddress.country}
                      onChange={(e) => updateAddress('country', e.target.value)}
                      className={inputClass}
                      placeholder="Country"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-2">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 border-2 border-cream-300 text-cocoa-700 px-5 py-2.5 rounded-full font-semibold text-sm hover:bg-cream-50 transition-all duration-300"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setStep(2)}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-clay-500 to-saffron-500 text-white px-6 py-2.5 rounded-full font-semibold text-sm shadow-warm hover:shadow-warm-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                Items & Payment
                <ArrowLeft className="w-4 h-4 rotate-180" />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6 animate-fadeInUp">
            {/* Items */}
            <div className="bg-white rounded-2xl border border-cream-200 shadow-sm">
              <div className="h-1.5 bg-gradient-to-r from-clay-500 to-saffron-500 rounded-t-2xl" />
              <div className="p-5 md:p-6">
                <SectionHeader icon={Package} title="Items" subtitle="Select from your own products only" />
                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <div
                      key={item.id}
                      ref={el => {
                        if (el) {
                          productDropdownRefs.current[item.id] = el;
                        }
                      }}
                      className="flex gap-3 items-start p-3 md:p-4 bg-cream-50 rounded-xl border border-cream-200"
                    >
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-12 gap-2">
                        <div className="sm:col-span-5 relative">
                          <input
                            type="text"
                            value={productSearchTerms[item.id] || item.name || ''}
                            onChange={(e) => handleProductSearchChange(item.id, e.target.value)}
                            onFocus={() => handleProductFocus(item.id)}
                            placeholder="Search your products..."
                            className={`w-full px-3 py-2 border rounded-lg text-sm bg-white ${
                              errors[`item_${index}_name`] ? 'border-red-300' : 'border-cream-300'
                            } focus:ring-2 focus:ring-clay-500 focus:border-transparent outline-none transition-all placeholder:text-cocoa-400`}
                          />
                          {loadingProducts && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <Loader className="w-3 h-3 animate-spin text-cocoa-400" />
                            </div>
                          )}
                          {activeItemId === item.id && productSearchResults[item.id] && productSearchResults[item.id].length > 0 && (
                            <div className="absolute z-20 w-full mt-1.5 bg-white border border-cream-200 rounded-xl shadow-warm-lg max-h-40 overflow-y-auto scrollbar-thin animate-slideDown">
                              {productSearchResults[item.id].map((product) => (
                                <div
                                  key={product._id}
                                  className="px-3 py-2 hover:bg-clay-50 cursor-pointer transition-colors"
                                  onClick={() => handleProductSelect(item.id, product)}
                                >
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-cocoa-800">{product.name}</span>
                                    <span className="text-xs font-semibold text-clay-600">₹{product.price}</span>
                                  </div>
                                  {product.sku && (
                                    <div className="text-xs text-cocoa-400">SKU: {product.sku}</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {errors[`item_${index}_name`] && (
                            <p className="text-red-500 text-xs mt-1">{errors[`item_${index}_name`]}</p>
                          )}
                        </div>
                        <div className="sm:col-span-3">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateItem(item.id, 'quantity', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg text-sm bg-white ${
                              errors[`item_${index}_quantity`] ? 'border-red-300' : 'border-cream-300'
                            } focus:ring-2 focus:ring-clay-500 focus:border-transparent outline-none transition-all`}
                            placeholder="Qty"
                            min="1"
                          />
                          {errors[`item_${index}_quantity`] && (
                            <p className="text-red-500 text-xs mt-1">{errors[`item_${index}_quantity`]}</p>
                          )}
                        </div>
                        <div className="sm:col-span-3">
                          <input
                            type="number"
                            value={item.price}
                            onChange={(e) => updateItem(item.id, 'price', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg text-sm bg-white ${
                              errors[`item_${index}_price`] ? 'border-red-300' : 'border-cream-300'
                            } focus:ring-2 focus:ring-clay-500 focus:border-transparent outline-none transition-all`}
                            placeholder="Price"
                            min="0"
                            step="0.01"
                          />
                          {errors[`item_${index}_price`] && (
                            <p className="text-red-500 text-xs mt-1">{errors[`item_${index}_price`]}</p>
                          )}
                        </div>
                        <div className="sm:col-span-1 flex sm:justify-center">
                          <button
                            type="button"
                            onClick={() => removeItem(item.id)}
                            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors self-start"
                            title="Remove item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addItem}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-clay-600 hover:bg-clay-50 rounded-full transition-colors text-sm font-semibold"
                >
                  <Plus className="w-4 h-4" />
                  Add Item
                </button>
              </div>
            </div>

            {/* Payment & Status */}
            <div className="bg-white rounded-2xl border border-cream-200 shadow-sm overflow-hidden">
              <div className="h-1.5 bg-gradient-to-r from-saffron-500 to-saffron-400" />
              <div className="p-5 md:p-6">
                <SectionHeader icon={CreditCard} title="Payment & Status" subtitle="Payment method and fulfilment status" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className={labelClass}>Payment Method</label>
                    <select
                      value={formData.paymentMethod}
                      onChange={(e) => updateField('paymentMethod', e.target.value)}
                      className={inputClass}
                    >
                      <option value="offline">Offline</option>
                      <option value="cash">Cash</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="upi">UPI</option>
                      <option value="card">Card</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Order Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => updateField('status', e.target.value)}
                      className={inputClass}
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="processing">Processing</option>
                      <option value="ready_to_ship">Ready to Ship</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Shipping Cost</label>
                    <input
                      type="number"
                      value={formData.shippingCost}
                      onChange={(e) => updateField('shippingCost', e.target.value)}
                      className={inputClass}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Totals */}
            <div className="bg-white rounded-2xl border border-cream-200 shadow-sm overflow-hidden">
              <div className="h-1.5 bg-gradient-to-r from-cocoa-500 to-clay-500" />
              <div className="p-5 md:p-6">
                <SectionHeader icon={Calculator} title="Totals" subtitle="Subtotal, taxes and final amount" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className={labelClass}>Subtotal</label>
                    <input
                      type="number"
                      value={formData.subtotal}
                      onChange={(e) => updateField('subtotal', e.target.value)}
                      className={`${inputClass} bg-cream-50 font-semibold`}
                      placeholder="0.00"
                      step="0.01"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Tax</label>
                    <input
                      type="number"
                      value={formData.tax}
                      onChange={(e) => updateField('tax', e.target.value)}
                      className={inputClass}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      Total Amount <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={formData.total}
                        onChange={(e) => updateField('total', e.target.value)}
                        className={`flex-1 ${errors.total ? inputErrorClass : inputClass} font-bold`}
                        placeholder="0.00"
                        step="0.01"
                      />
                      <button
                        type="button"
                        onClick={calculateTotal}
                        className="px-4 py-2 bg-gradient-to-r from-clay-100 to-saffron-100 text-clay-700 rounded-xl hover:shadow-warm transition-all whitespace-nowrap font-semibold text-sm"
                      >
                        Calculate
                      </button>
                    </div>
                    {errors.total && (
                      <p className="text-red-500 text-xs mt-1">{errors.total}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="bg-white rounded-2xl border border-cream-200 shadow-sm overflow-hidden">
              <div className="h-1.5 bg-gradient-to-r from-clay-400 to-saffron-400" />
              <div className="p-5 md:p-6">
                <label className={labelClass}>Notes (Optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  rows="2"
                  className={`${inputClass} resize-none`}
                  placeholder="Any special instructions or notes..."
                />
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="inline-flex items-center gap-2 border-2 border-cream-300 text-cocoa-700 px-5 py-2.5 rounded-full font-semibold text-sm hover:bg-cream-50 transition-all duration-300"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <button
                type="button"
                onClick={handleGenerate}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-clay-500 to-saffron-500 text-white px-6 py-2.5 rounded-full font-semibold text-sm shadow-warm hover:shadow-warm-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                <Package className="w-4 h-4" />
                Generate Packing Slip
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="text-center py-12 animate-fadeInUp">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 mb-4">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h4 className="text-xl font-display font-bold text-cocoa-900">Almost there!</h4>
            <p className="text-cocoa-500 mt-1">Review and generate your packing slip</p>
          </div>
        )}
      </div>
    </div>
  );

  // Render packing slip
  const renderPackingSlip = () => (
    <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-gradient-to-br from-cream-100 via-cream-50 to-saffron-50 scrollbar-thin">
      <div
        ref={printRef}
        id="print-area"
        className="bg-white rounded-2xl shadow-warm p-8 max-w-2xl mx-auto print:shadow-none print:p-6"
        style={{ width: '210mm', minHeight: '148mm' }}
      >
        <div className="space-y-6">
          {/* Header with Logo */}
          <div className="flex justify-between items-start border-b-2 border-clay-200 pb-4">
            <div className="flex items-center gap-3">
              <img
                src={TantikaLogo}
                alt="তন্তিকা"
                className="h-16 w-auto object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div>
                <h1 className="text-2xl font-display font-bold bg-gradient-to-r from-clay-600 to-saffron-600 bg-clip-text text-transparent">তন্তিকা</h1>
                <p className="text-sm text-cocoa-500">Handcrafted with ❤️</p>
                <p className="text-sm text-cocoa-500">https://tantikacrafts.netlify.app</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-display font-bold tracking-widest text-cocoa-800">PACKING SLIP</div>
              <div className="text-xs text-cocoa-400 mt-1">Date: {formatDate(new Date())}</div>
              <div className="text-xs text-cocoa-400">Order: #{formData.orderNumber}</div>
              <span className={`inline-block mt-2 px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(formData.status)}`}>
                {getStatusLabel(formData.status)}
              </span>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <h4 className="text-sm font-display font-bold text-cocoa-800 flex items-center gap-2">
                <User className="w-4 h-4 text-clay-600" />
                Customer Details
              </h4>
              <div className="bg-cream-50 border border-cream-100 p-3 rounded-xl space-y-1 text-sm">
                <p className="font-semibold text-cocoa-900">{formData.customerName}</p>
                <p className="text-cocoa-600 flex items-center gap-1">
                  <Phone className="w-3 h-3" /> {formData.customerPhone}
                </p>
                {formData.customerEmail && (
                  <p className="text-cocoa-600 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {formData.customerEmail}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-display font-bold text-cocoa-800 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-clay-600" />
                Shipping Address
              </h4>
              <div className="bg-cream-50 border border-cream-100 p-3 rounded-xl text-sm">
                <p className="font-semibold text-cocoa-900">{formData.customerName}</p>
                <p className="text-cocoa-600 mt-1">
                  {formData.shippingAddress.street}
                </p>
                <p className="text-cocoa-600">
                  {formData.shippingAddress.city}, {formData.shippingAddress.state} - {formData.shippingAddress.postalCode}
                </p>
                <p className="text-cocoa-600">{formData.shippingAddress.country}</p>
              </div>
            </div>
          </div>

          {/* Order Details */}
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <h4 className="text-sm font-display font-bold text-cocoa-800 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-clay-600" />
                Order Information
              </h4>
              <div className="bg-cream-50 border border-cream-100 p-3 rounded-xl space-y-1 text-sm">
                <p className="text-cocoa-700"><span className="font-semibold text-cocoa-900">Order Date:</span> {formatDate(formData.orderDate)}</p>
                <p className="text-cocoa-700"><span className="font-semibold text-cocoa-900">Artisan:</span> {formData.artisanName || 'Not specified'}</p>
                <p className="text-cocoa-700"><span className="font-semibold text-cocoa-900">Payment:</span> {formData.paymentMethod}</p>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-display font-bold text-cocoa-800 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-clay-600" />
                Payment Summary
              </h4>
              <div className="bg-cream-50 border border-cream-100 p-3 rounded-xl space-y-1 text-sm">
                <p className="text-cocoa-700 flex justify-between"><span className="font-semibold text-cocoa-900">Subtotal:</span> {formatCurrency(formData.subtotal)}</p>
                <p className="text-cocoa-700 flex justify-between"><span className="font-semibold text-cocoa-900">Tax:</span> {formatCurrency(formData.tax)}</p>
                <p className="text-cocoa-700 flex justify-between"><span className="font-semibold text-cocoa-900">Shipping:</span> {formatCurrency(formData.shippingCost)}</p>
                <p className="font-bold text-clay-700 border-t border-clay-200 pt-1 flex justify-between">
                  <span>Total</span>
                  <span>{formatCurrency(formData.total)}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="space-y-2">
            <h4 className="text-sm font-display font-bold text-cocoa-800 flex items-center gap-2">
              <Package className="w-4 h-4 text-clay-600" />
              Items
              <span className="text-xs text-cocoa-400 font-normal">(Your products only)</span>
            </h4>
            <div className="border border-cream-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-cream-50">
                  <tr>
                    <th className="text-left px-3 py-2.5 font-semibold text-cocoa-600">#</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-cocoa-600">Item</th>
                    <th className="text-center px-3 py-2.5 font-semibold text-cocoa-600">Qty</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-cocoa-600">Price</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-cocoa-600">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, index) => (
                    <tr key={item.id} className="border-t border-cream-100">
                      <td className="px-3 py-2.5 text-cocoa-400">{index + 1}</td>
                      <td className="px-3 py-2.5">
                        <div>
                          <div className="font-medium text-cocoa-900">{item.name}</div>
                          {item.sku && <div className="text-xs text-cocoa-400">SKU: {item.sku}</div>}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-center text-cocoa-800">{item.quantity}</td>
                      <td className="px-3 py-2.5 text-right text-cocoa-800">{formatCurrency(item.price)}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-cocoa-900">
                        {formatCurrency((parseFloat(item.price) || 0) * (parseInt(item.quantity) || 0))}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-cream-50 border-t border-cream-200">
                  <tr>
                    <td colSpan="4" className="px-3 py-2 text-right font-semibold text-cocoa-700">Subtotal</td>
                    <td className="px-3 py-2 text-right font-semibold text-cocoa-900">{formatCurrency(formData.subtotal)}</td>
                  </tr>
                  <tr>
                    <td colSpan="4" className="px-3 py-2 text-right text-sm text-cocoa-500">Shipping</td>
                    <td className="px-3 py-2 text-right text-sm text-cocoa-700">{formatCurrency(formData.shippingCost)}</td>
                  </tr>
                  <tr>
                    <td colSpan="4" className="px-3 py-2 text-right text-sm text-cocoa-500">Tax</td>
                    <td className="px-3 py-2 text-right text-sm text-cocoa-700">{formatCurrency(formData.tax)}</td>
                  </tr>
                  <tr className="border-t-2 border-clay-400">
                    <td colSpan="4" className="px-3 py-2.5 text-right font-bold text-clay-700">Total</td>
                    <td className="px-3 py-2.5 text-right font-bold text-clay-700">{formatCurrency(formData.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* QR Code */}
          <div className="flex justify-between items-center border-t-2 border-cream-200 pt-4">
            <div className="text-xs text-cocoa-400">
              <p>Thank you for shopping with তন্তিকা!</p>
              <p className="mt-1">Scan the QR code to visit our website</p>
            </div>
            <div className="flex items-center gap-4">
              {loading ? (
                <div className="w-20 h-20 bg-cream-100 rounded-xl flex items-center justify-center">
                  <Loader className="w-6 h-6 animate-spin text-clay-600" />
                </div>
              ) : qrError ? (
                <div className="w-20 h-20 bg-cream-100 rounded-xl flex items-center justify-center flex-col">
                  <AlertCircle className="w-6 h-6 text-red-500" />
                  <span className="text-xs text-red-500">QR Error</span>
                </div>
              ) : qrCodeUrl ? (
                <img
                  src={qrCodeUrl}
                  alt="Visit তন্তিকা Website"
                  className="w-20 h-20 border-2 border-cream-200 rounded-xl"
                  crossOrigin="anonymous"
                />
              ) : (
                <div className="w-20 h-20 bg-cream-100 rounded-xl flex items-center justify-center">
                  <QrCode className="w-8 h-8 text-cocoa-300" />
                </div>
              )}
              <div className="text-xs text-cocoa-500">
                <p className="font-semibold text-cocoa-800">Scan to Visit Us!</p>
                <p className="text-cocoa-400">তন্তিকা Crafts</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center text-xs text-cocoa-400 border-t border-cream-200 pt-4">
            <p>Generated on {formatDate(new Date())}</p>
            <p className="mt-1">This is a system generated packing slip for order #{formData.orderNumber}</p>
            {formData.notes && (
              <p className="mt-1 text-clay-600 font-medium">📝 {formData.notes}</p>
            )}
            <p className="mt-1">Thank you for supporting handcrafted products! 🙏</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-cocoa-950/60 backdrop-blur-sm flex items-center justify-center p-3 md:p-4 z-50">
      <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col shadow-2xl animate-slideUp">
        {/* Header */}
        <div className="sticky top-0 z-20 bg-gradient-to-br from-cream-50 via-white to-saffron-50 border-b border-cream-200 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-gradient-to-br from-clay-500 to-saffron-500 text-white shadow-warm">
              <Package className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-display font-bold text-cocoa-900">
                {showForm ? 'My Packing Slip' : `Packing Slip — ${formData.orderNumber}`}
              </h3>
              <p className="text-sm text-cocoa-500">
                {showForm ? 'Create a packing slip for an offline sale' : 'Review and print the packing slip'}
              </p>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1.5 ml-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
              <Shield className="w-3.5 h-3.5" />
              Artisan
            </span>
          </div>
          <div className="flex items-center gap-2">
            {!showForm && (
              <>
                <button
                  type="button"
                  onClick={handlePrint}
                  className="inline-flex items-center gap-2 border-2 border-cream-300 text-cocoa-700 px-4 py-2 rounded-full font-semibold text-sm hover:bg-cream-50 transition-all duration-300"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </button>
                <button
                  type="button"
                  onClick={handleGeneratePDF}
                  disabled={generatingPDF}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-clay-500 to-saffron-500 text-white px-4 py-2 rounded-full font-semibold text-sm shadow-warm hover:shadow-warm-lg hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:hover:translate-y-0"
                >
                  {generatingPDF ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Download PDF
                </button>
                <button
                  type="button"
                  onClick={handleEdit}
                  className="inline-flex items-center gap-2 bg-white border-2 border-clay-200 text-clay-700 px-4 py-2 rounded-full font-semibold text-sm hover:bg-clay-50 transition-all duration-300"
                >
                  <Edit3 className="w-4 h-4" />
                  Edit
                </button>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-cream-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-cocoa-500" />
            </button>
          </div>
        </div>

        {showForm ? renderForm() : renderPackingSlip()}
      </div>
    </div>
  );
};

export default ArtisanAdHocPackingSlip;
